import React from 'react';
import { NavLink } from 'react-router-dom';
import BgColorContainer from '../../Layout/BgColorContainer';
import LazyVideo from '../../ui/cinematic/LazyVideo';
import PillLink from '../../ui/cinematic/PillLink';
import useReveal from '../../../hooks/useReveal';
import useCursorZone from '../../../hooks/useCursorZone';
import { usePaginas } from '../../Layout/Navbar';
import { elemento, partes, useTextos } from '../../../cms/textos';
import RESPALDO from '../../../Data/Paginas/paginaComun.json';
// El mismo archivo del hero: ya está en caché cuando el usuario llega acá
import heroVideo from '../../../assets/home/hero-scrub.mp4';
import bannerVideo from '../../../assets/home/footer-banner.mp4';
import wordmark from '../../../assets/LogoWhite3.webp';
import { useAjustes } from '../../../cms/ajustes';
import { abrirPreferenciasCookies } from '../../../cms/cookies';


// Las páginas de la columna de links (todas menos Inicio)
const LINKS = ['nosotros', 'servicios', 'portafolio', 'analisis', 'blog', 'contacto'];

/**
 * `ceja` y `bajada` permiten que una página traiga su propio llamado (el "mensaje"
 * de los detalles de servicio) sin sumar un segundo CTA antes del footer; si no
 * vienen, van los de "Páginas → Menú, cierre y 404".
 * `sinLlamado` lo quita entero: en Contacto repetía el llamado y el video del hero.
 */
function Footer({ ceja, bajada, sinLlamado = false }) {
    const { agenda, correo, redes } = useAjustes();
    const textos = useTextos('paginaComun', RESPALDO).cierre;
    const paginas = usePaginas(LINKS);
    const Ceja = elemento(textos, 'ceja', 'p');
    const Titulo = elemento(textos, 'titular', 'h2');
    const Bajada = elemento(textos, 'bajada', 'p');
    const Descripcion = elemento(textos, 'descripcion', 'p');
    const Derechos = elemento(textos, 'derechos', 'p');
    const ctaRef = useReveal();
    const linksRef = useReveal();
    const cursorZone = useCursorZone('dark');

    return (
        <BgColorContainer>
            <footer className="relative overflow-hidden bg-ink text-cream" {...cursorZone}>
                {/* CTA de cierre sobre video, sin overlay (el clip ya es oscuro) */}
                {!sinLlamado && (
                <div className="relative flex min-h-[90svh] items-center justify-center overflow-hidden px-5">
                    <LazyVideo src={heroVideo} className="absolute inset-0 h-full w-full object-cover" />
                    <div ref={ctaRef} className="relative flex flex-col items-center gap-8 text-center">
                        <Ceja data-reveal className="text-xs uppercase tracking-[0.3em] text-cream/60 md:text-sm">
                            {ceja || textos.ceja}
                        </Ceja>
                        {/* Cada parte del titular en su línea; la de *asteriscos*, en serif */}
                        <Titulo data-reveal data-sanity-edit-target="" className="text-[17vw] font-medium leading-[0.9] tracking-tight md:text-[11vw] xl:text-[10rem]">
                            {partes(textos.titular).map((p, i) => (
                                <React.Fragment key={i}>
                                    {i > 0 && <>{' '}<br /></>}
                                    {p.accent ? <span className="font-serif font-normal italic">{p.text}</span> : p.text}
                                </React.Fragment>
                            ))}
                        </Titulo>
                        {(bajada || textos.bajada) && (
                            <Bajada data-reveal className="max-w-xl text-base leading-relaxed text-cream/75 md:text-lg">
                                {bajada || textos.bajada}
                            </Bajada>
                        )}
                        <div data-reveal className="flex flex-wrap justify-center gap-3">
                            <PillLink href={agenda} external>{textos.boton}</PillLink>
                            <PillLink href={`mailto:${correo}`} variant="ghost">{correo}</PillLink>
                        </div>
                    </div>
                </div>
                )}

                <div ref={linksRef} className="container relative flex flex-col gap-16 px-5 pb-10 pt-20 md:px-10 2xl:px-0">
                    <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
                        <Descripcion data-reveal className="max-w-sm text-base leading-relaxed text-cream/50 md:col-span-4">
                            {textos.descripcion}
                        </Descripcion>
                        <nav data-reveal aria-label="Secciones del sitio" className="md:col-span-4 md:col-start-6">
                            <ul className="grid grid-cols-2 gap-x-10 gap-y-3">
                                {paginas.map(({ label, to }) => (
                                    <li key={to}>
                                        <NavLink to={to} className="group inline-flex flex-col text-lg text-cream/70 transition-colors duration-300 hover:text-cream">
                                            {label}
                                            <span className="h-px w-0 bg-cream transition-all duration-500 ease-expo group-hover:w-full" />
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                        <ul data-reveal aria-label="Redes sociales" className="flex flex-col gap-3 md:col-span-2 md:col-start-11">
                            {redes.map(({ name, url }) => (
                                <li key={name}>
                                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-lg text-cream/70 transition-colors duration-300 hover:text-cream">
                                        {name} <span aria-hidden="true">↗</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="flex flex-col gap-4 border-t border-cream/15 pt-8 text-sm text-cream/40 md:flex-row md:items-center md:justify-between">
                        <Derechos>© {new Date().getFullYear()} Awna. {textos.derechos}</Derechos>
                        <div className="flex flex-wrap gap-x-6 gap-y-2">
                            <button type="button" onClick={abrirPreferenciasCookies} className="transition-colors duration-300 hover:text-cream">
                                {textos.preferencias}
                            </button>
                            <NavLink to="/politica-de-privacidad" className="transition-colors duration-300 hover:text-cream">
                                {textos.privacidad}
                            </NavLink>
                        </div>
                    </div>
                </div>

                {/* Banner de cierre en movimiento: un recorte propio del metaball
                    (1600×270, 284 KB) con el logotipo encima. Más liviano que
                    reproducir el video del hero ampliado. */}
                <div className="relative h-[17vw] min-h-[140px] overflow-hidden">
                    <LazyVideo src={bannerVideo} className="absolute inset-0 h-full w-full object-cover" />
                    <img src={wordmark} alt="Awna" loading="lazy" className="absolute left-1/2 top-1/2 w-[14vw] min-w-[110px] -translate-x-1/2 -translate-y-1/2" />
                </div>
            </footer>
        </BgColorContainer>
    );
}

export default Footer;
